(() => {
  const padNumber = (value) => String(value).padStart(2, '0');

  const initIineCarousels = (scope = document) => {
    scope.querySelectorAll('[data-iine-carousel]').forEach((carousel) => {
      if (carousel.dataset.iineCarouselReady === 'true') return;
      const track = carousel.querySelector('[data-iine-carousel-track]');
      const previous = carousel.querySelector('[data-iine-carousel-prev]');
      const next = carousel.querySelector('[data-iine-carousel-next]');
      const currentLabel = carousel.querySelector('[data-iine-carousel-current]');
      const totalLabel = carousel.querySelector('[data-iine-carousel-total]');
      const progress = carousel.querySelector('[data-iine-carousel-progress]');
      const pagination = carousel.querySelector('[data-iine-carousel-pagination]');
      const originals = track ? [...track.querySelectorAll('[data-iine-carousel-item]')] : [];
      if (!track || !originals.length) return;

      const total = originals.length;
      const loopBuffer = total > 1
        ? Math.min(total, Math.max(1, Number(carousel.dataset.loopBuffer || 1)))
        : 0;
      const autoplayDelay = Number(carousel.dataset.autoplay || 0);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const paginationDots = [];
      if (pagination) {
        pagination.replaceChildren();
        originals.forEach(() => {
          const dot = document.createElement('span');
          pagination.appendChild(dot);
          paginationDots.push(dot);
        });
      }
      let items = originals;
      let itemIndex = 0;
      let autoTimer = 0;
      let settleTimer = 0;
      let scrollTimer = 0;
      let interactionPaused = false;
      let inView = !('IntersectionObserver' in window);

      const makeClone = (item) => {
        const clone = item.cloneNode(true);
        clone.removeAttribute('data-iine-carousel-item');
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.dataset.iineCarouselClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, iframe, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
        return clone;
      };

      if (total > 1) {
        const leadingClones = document.createDocumentFragment();
        originals.slice(-loopBuffer).forEach((item) => leadingClones.appendChild(makeClone(item)));
        const trailingClones = document.createDocumentFragment();
        originals.slice(0, loopBuffer).forEach((item) => trailingClones.appendChild(makeClone(item)));
        track.prepend(leadingClones);
        track.append(trailingClones);
        items = [...track.children];
        itemIndex = loopBuffer;
      }

      const itemLeft = (item) => {
        const itemRect = item.getBoundingClientRect();
        const trackRect = track.getBoundingClientRect();
        return itemRect.left - trackRect.left + track.scrollLeft;
      };

      const originalIndex = () => {
        if (total <= 1) return 0;
        return ((itemIndex - loopBuffer) % total + total) % total;
      };

      const updateStatus = () => {
        const displayIndex = originalIndex();
        if (currentLabel) currentLabel.textContent = padNumber(displayIndex + 1);
        if (totalLabel) totalLabel.textContent = padNumber(total);
        const ratio = total ? (displayIndex + 1) / total : 1;
        carousel.style.setProperty('--iine-carousel-progress', String(ratio));
        if (progress) progress.style.transform = `scaleX(${ratio})`;
        paginationDots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === displayIndex);
          if (index === displayIndex) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      const scrollInstantly = (left) => {
        const previousBehavior = track.style.scrollBehavior;
        const previousSnapType = track.style.scrollSnapType;
        track.style.scrollBehavior = 'auto';
        track.style.scrollSnapType = 'none';
        track.scrollLeft = left;
        track.getBoundingClientRect();
        track.style.scrollBehavior = previousBehavior;
        track.style.scrollSnapType = previousSnapType;
      };

      const jumpTo = (index) => {
        itemIndex = index;
        scrollInstantly(itemLeft(items[itemIndex]));
        updateStatus();
      };

      const settleLoop = () => {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => {
          if (total <= 1) return;

          let rebasedIndex = itemIndex;
          if (itemIndex < loopBuffer) rebasedIndex += total;
          else if (itemIndex >= loopBuffer + total) rebasedIndex -= total;
          if (rebasedIndex === itemIndex) return;

          const relativeOffset = track.scrollLeft - itemLeft(items[itemIndex]);
          itemIndex = rebasedIndex;
          scrollInstantly(itemLeft(items[itemIndex]) + relativeOffset);
          updateStatus();
        }, reducedMotion ? 0 : 40);
      };

      const goTo = (index, behavior = 'smooth') => {
        if (total <= 1) return;
        itemIndex = Math.max(0, Math.min(items.length - 1, index));
        track.scrollTo({ left: itemLeft(items[itemIndex]), behavior: reducedMotion ? 'auto' : behavior });
        updateStatus();
        if (reducedMotion) settleLoop();
      };

      const clearAutoplay = () => window.clearTimeout(autoTimer);
      const scheduleAutoplay = () => {
        clearAutoplay();
        if (reducedMotion || autoplayDelay < 1000 || interactionPaused || !inView || document.hidden || total <= 1) return;
        autoTimer = window.setTimeout(() => {
          goTo(itemIndex + 1);
          scheduleAutoplay();
        }, autoplayDelay);
      };

      const pause = () => {
        interactionPaused = true;
        clearAutoplay();
      };
      const resume = () => {
        interactionPaused = false;
        scheduleAutoplay();
      };

      previous?.addEventListener('click', () => {
        goTo(itemIndex - 1);
        scheduleAutoplay();
      });
      next?.addEventListener('click', () => {
        goTo(itemIndex + 1);
        scheduleAutoplay();
      });

      carousel.addEventListener('mouseenter', pause);
      carousel.addEventListener('mouseleave', resume);
      carousel.addEventListener('focusin', pause);
      carousel.addEventListener('focusout', (event) => {
        if (!carousel.contains(event.relatedTarget)) resume();
      });
      track.addEventListener('pointerdown', pause);
      track.addEventListener('pointerup', resume);
      track.addEventListener('pointercancel', resume);
      track.addEventListener('scroll', () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          let nearest = 0;
          let nearestDistance = Number.POSITIVE_INFINITY;
          items.forEach((item, index) => {
            const distance = Math.abs(itemLeft(item) - track.scrollLeft);
            if (distance < nearestDistance) {
              nearest = index;
              nearestDistance = distance;
            }
          });
          itemIndex = nearest;
          updateStatus();
          settleLoop();
        }, 100);
      }, { passive: true });

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          inView = entries[0]?.isIntersecting || false;
          if (inView) scheduleAutoplay();
          else clearAutoplay();
        }, { threshold: 0.15 });
        observer.observe(carousel);
      }

      document.addEventListener('visibilitychange', scheduleAutoplay);
      window.addEventListener('resize', () => jumpTo(itemIndex), { passive: true });
      window.requestAnimationFrame(() => {
        jumpTo(itemIndex);
        scheduleAutoplay();
      });
      if (total <= 1) {
        previous?.setAttribute('disabled', '');
        next?.setAttribute('disabled', '');
      }
      carousel.dataset.iineCarouselReady = 'true';
    });
  };

  const respectReducedMotion = () => {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('.iine-hero__video').forEach((video) => video.pause());
  };

  let iineHeroVideoObserver;

  const initIineHeroVideoPlayback = (scope = document) => {
    const videos = scope.querySelectorAll('.iine-hero__video');
    if (!videos.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      if (reducedMotion) videos.forEach((video) => video.pause());
      return;
    }

    if (!iineHeroVideoObserver) {
      iineHeroVideoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && !document.hidden && video.getClientRects().length) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.01, rootMargin: '80px 0px' });
    }

    videos.forEach((video) => {
      if (video.dataset.iinePlaybackReady === 'true') return;
      iineHeroVideoObserver.observe(video);
      video.dataset.iinePlaybackReady = 'true';
    });
  };

  let iineSectionMotionObserver;

  const initIineSectionMotion = (scope = document) => {
    const sectionSelector = [
      '.template-index .iine-hot-sale',
      '.template-index .iine-featured',
      '.template-index .iine-reviews',
      '.template-index .iine-reddit-voices',
      '.template-index .iine-showcase',
      '.template-index .iine-ways',
      '.template-index .iine-action'
    ].join(', ');
    const sections = [...scope.querySelectorAll(sectionSelector)];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    sections.forEach((section) => {
      if (section.dataset.iineMotionReady === 'true') return;
      // Only animate the compact heading. Transforming the entire shell created
      // very large composited layers (and nested the marquee/carousel layers),
      // which made vertical scrolling stutter on Safari and mobile GPUs.
      const surface = section.querySelector(':scope > .iine-shell > .iine-section-heading')
        || section.querySelector('.iine-section-heading');
      section.dataset.iineMotionReady = 'true';
      if (!surface || reducedMotion || !('IntersectionObserver' in window)) {
        section.classList.add('is-in-view');
        return;
      }

      section.classList.add('iine-motion-section');
      surface.classList.add('iine-motion-surface');
      if (!iineSectionMotionObserver) {
        iineSectionMotionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle('is-in-view', entry.isIntersecting && entry.intersectionRatio >= 0.08);
          });
        }, { threshold: [0, 0.08, 0.18], rootMargin: '-3% 0px -5% 0px' });
      }
      iineSectionMotionObserver.observe(section);
    });
  };

  const normalizedText = (element) => element?.textContent?.replace(/\s+/g, ' ').trim() || '';

  const firstText = (root, selectors) => {
    for (const selector of selectors) {
      const value = normalizedText(root.querySelector(selector));
      if (value) return value;
    }
    return '';
  };

  const reviewProductChecks = new Map();

  const getReviewRating = (review) => {
    const ratingElement = review.querySelector('.jdgm-rev__rating, .jdgm-carousel-item__review-rating, [data-score], [aria-label*="out of 5"]');
    const rawRating = ratingElement?.dataset?.score
      || ratingElement?.getAttribute('data-score')
      || review.dataset?.score
      || review.dataset?.rating
      || review.getAttribute('data-rating')
      || ratingElement?.getAttribute('aria-label')?.match(/[0-5](?:\.\d+)?/)?.[0]
      || '';
    const parsedRating = Number.parseFloat(rawRating);
    if (Number.isFinite(parsedRating)) return parsedRating;
    return review.querySelectorAll('.jdgm-star.jdgm--on').length;
  };

  const getReviewProductUrl = (review) => {
    const productAnchor = review.querySelector([
      '.jdgm-rev__prod-info a[href]',
      'a.jdgm-rev__prod-info[href]',
      '.jdgm-rev__product-title[href]',
      '.jdgm-rev__product-title a[href]',
      '.jdgm-carousel-item__product[href]',
      '.jdgm-carousel-item__product a[href]',
      'a[href*="/products/"]'
    ].join(', '));
    const rawUrl = productAnchor?.getAttribute('href') || review.getAttribute('data-product-url') || '';
    if (!rawUrl || rawUrl === '#') return null;
    try {
      const url = new URL(rawUrl, window.location.origin);
      if (url.origin !== window.location.origin || !/\/products\/[^/?#]+/i.test(url.pathname)) return null;
      return {
        anchor: productAnchor,
        href: `${url.pathname}${url.search}`
      };
    } catch (error) {
      return null;
    }
  };

  const reviewProductIsLive = (href) => {
    if (reviewProductChecks.has(href)) return reviewProductChecks.get(href);
    const check = window.fetch(href, {
      method: 'HEAD',
      credentials: 'same-origin',
      cache: 'force-cache',
      headers: { Accept: 'text/html' }
    }).then((response) => {
      if (!response.ok) return false;
      const finalPath = new URL(response.url, window.location.origin).pathname;
      return /\/products\/[^/?#]+/i.test(finalPath) && !/(^|\/)404(?:\/|$)/i.test(finalPath);
    }).catch(() => false);
    reviewProductChecks.set(href, check);
    return check;
  };

  const buildReviewCard = (review) => {
    const rating = getReviewRating(review);
    const productLink = getReviewProductUrl(review);
    if (rating < 4 || !productLink) return null;

    const title = firstText(review, ['.jdgm-rev__title', '.jdgm-carousel-item__review-title']);
    const body = firstText(review, ['.jdgm-rev__body', '.jdgm-carousel-item__review-body', '.jdgm-rev__content']);
    const author = firstText(review, ['.jdgm-rev__author', '.jdgm-carousel-item__reviewer-name', '[data-reviewer-name]']) || 'Verified buyer';
    const productHandle = productLink.href.split('/products/')[1]?.split(/[?#]/)[0] || '';
    const product = firstText(review, ['.jdgm-rev__prod-info', '.jdgm-rev__product-title', '.jdgm-carousel-item__product', '[data-product-title]'])
      || normalizedText(productLink.anchor)
      || decodeURIComponent(productHandle).replace(/-/g, ' ');
    const quote = [title, body].filter(Boolean).join(' — ');
    if (!quote) return null;

    const card = document.createElement('a');
    card.className = 'iine-review-card';
    card.setAttribute('aria-label', `${rating} out of 5 stars, review by ${author}`);
    card.setAttribute('href', productLink.href);

    const quoteElement = document.createElement('p');
    quoteElement.className = 'iine-review-card__quote';
    quoteElement.textContent = quote;

    const reviewer = document.createElement('div');
    reviewer.className = 'iine-review-card__reviewer';
    const avatar = document.createElement('span');
    avatar.className = 'iine-review-card__avatar';
    const sourceAvatar = review.querySelector('.jdgm-rev__icon img, .jdgm-rev__buyer-picture img, .jdgm-carousel-item__reviewer-avatar img, img.jdgm-carousel-item__reviewer-avatar');
    if (sourceAvatar?.src) {
      const image = document.createElement('img');
      image.src = sourceAvatar.src;
      image.alt = '';
      image.loading = 'lazy';
      avatar.appendChild(image);
    } else {
      avatar.textContent = author.trim().charAt(0).toUpperCase() || 'I';
    }

    const identity = document.createElement('span');
    identity.className = 'iine-review-card__identity';
    const name = document.createElement('strong');
    name.textContent = author;
    const productName = document.createElement('small');
    productName.textContent = product;
    identity.append(name, productName);
    reviewer.append(avatar, identity);
    card.append(quoteElement, reviewer);
    return { card, productUrl: productLink.href };
  };

  const initIineReviewWalls = (scope = document) => {
    scope.querySelectorAll('[data-iine-review-wall]').forEach(async (wall) => {
      if (wall.dataset.iineReady === 'true' || wall.dataset.iineLoading === 'true') return;
      wall.dataset.iineLoading = 'true';
      const source = wall.querySelector('[data-iine-review-source]');
      const rows = wall.querySelector('[data-iine-review-rows]');
      const fallback = wall.querySelector('[data-iine-review-fallback]');
      if (!source || !rows) {
        delete wall.dataset.iineLoading;
        return;
      }

      let reviewNodes = [...source.querySelectorAll('.jdgm-rev, .jdgm-all-reviews-page__review')];
      if (!reviewNodes.length) reviewNodes = [...source.querySelectorAll('.jdgm-carousel-item')];
      if (!reviewNodes.length) reviewNodes = [...source.querySelectorAll('[data-review-id]')];

      const seen = new Set();
      const candidates = [];
      reviewNodes.forEach((review) => {
        const candidate = buildReviewCard(review);
        if (!candidate) return;
        const key = candidate.card.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        candidates.push(candidate);
      });

      const cards = (await Promise.all(candidates.map(async (candidate) => (
        (await reviewProductIsLive(candidate.productUrl)) ? candidate.card : null
      )))).filter(Boolean);

      if (!cards.length) {
        delete wall.dataset.iineLoading;
        if (!wall.iineReviewObserver && 'MutationObserver' in window) {
          wall.iineReviewObserver = new MutationObserver(() => initIineReviewWalls(document));
          wall.iineReviewObserver.observe(source, { childList: true, subtree: true });
        }
        const attempts = Number(wall.dataset.iineReviewAttempts || 0);
        if (attempts < 60) {
          wall.dataset.iineReviewAttempts = String(attempts + 1);
          window.setTimeout(() => initIineReviewWalls(document), 500);
        }
        return;
      }
      const midpoint = Math.ceil(cards.length / 2);
      let rowCards = [cards.slice(0, midpoint), cards.slice(midpoint)];
      if (!rowCards[1].length) rowCards[1] = cards.map((card) => card.cloneNode(true));

      rows.querySelectorAll('[data-iine-review-row]').forEach((row, rowIndex) => {
        const track = row.querySelector('.iine-review-marquee__track');
        let items = rowCards[rowIndex];
        while (items.length < 6) items = [...items, ...items.map((card) => card.cloneNode(true))];
        const reviewSet = document.createElement('div');
        reviewSet.className = 'iine-review-marquee__set';
        items.forEach((card) => reviewSet.appendChild(card.cloneNode(true)));
        const duplicateSet = reviewSet.cloneNode(true);
        duplicateSet.setAttribute('aria-hidden', 'true');
        duplicateSet.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });
        track.replaceChildren(reviewSet, duplicateSet);
        row.style.setProperty('--iine-review-duration', '60s');
      });

      if (fallback) fallback.hidden = true;
      rows.hidden = false;
      wall.classList.add('is-ready');
      wall.removeAttribute('data-iine-review-attempts');
      wall.iineReviewObserver?.disconnect();
      wall.dataset.iineReady = 'true';
      delete wall.dataset.iineLoading;
    });
  };

  const clamp = (value, minimum = 0, maximum = 1) => Math.min(maximum, Math.max(minimum, value));

  const initIineClosingScenes = (scope = document) => {
    scope.querySelectorAll('[data-iine-closing]').forEach((section) => {
      if (section.dataset.iineClosingReady === 'true') return;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let frame = 0;
      let isActive = !('IntersectionObserver' in window);
      let scrollDistance = 1;
      let isMobile = window.innerWidth < 768;

      const measure = () => {
        scrollDistance = Math.max(1, section.offsetHeight - window.innerHeight);
        isMobile = window.innerWidth < 768;
      };

      const update = () => {
        frame = 0;
        if (reducedMotion || !isActive) return;
        const progress = clamp(-section.getBoundingClientRect().top / scrollDistance);
        const imageProgress = clamp(progress / 0.76);
        const imageEase = 1 - Math.pow(1 - imageProgress, 3);
        const contentProgress = clamp((progress - 0.44) / 0.36);
        const contentEase = 1 - Math.pow(1 - contentProgress, 3);
        const initialClip = isMobile ? 9 : 15;
        const initialScale = isMobile ? 1.12 : 1.18;
        section.style.setProperty('--iine-closing-clip', `${(initialClip * (1 - imageEase)).toFixed(3)}%`);
        section.style.setProperty('--iine-closing-radius', `${(14 * (1 - imageEase)).toFixed(3)}px`);
        section.style.setProperty('--iine-closing-image-scale', (initialScale - (initialScale - 1) * imageEase).toFixed(4));
        section.style.setProperty('--iine-closing-content-opacity', contentEase.toFixed(4));
        section.style.setProperty('--iine-closing-content-scale', (0.88 + 0.12 * contentEase).toFixed(4));
        section.style.setProperty('--iine-closing-content-y', `${(-42 - 8 * contentEase).toFixed(3)}%`);
      };

      const requestUpdate = () => {
        if (isActive && !frame) frame = window.requestAnimationFrame(update);
      };

      const handleResize = () => {
        measure();
        requestUpdate();
      };

      section.classList.add('is-scroll-ready');
      measure();
      window.addEventListener('resize', handleResize, { passive: true });

      if ('IntersectionObserver' in window && !reducedMotion) {
        const observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          const nextActive = entry?.isIntersecting || false;
          if (nextActive === isActive) return;

          isActive = nextActive;
          section.classList.toggle('is-motion-active', isActive);
          if (isActive) {
            measure();
            window.addEventListener('scroll', requestUpdate, { passive: true });
            requestUpdate();
          } else {
            window.removeEventListener('scroll', requestUpdate);
            if (frame) window.cancelAnimationFrame(frame);
            frame = 0;
          }
        }, { threshold: 0, rootMargin: '125% 0px' });
        observer.observe(section);
      } else if (!reducedMotion) {
        section.classList.add('is-motion-active');
        window.addEventListener('scroll', requestUpdate, { passive: true });
        requestUpdate();
      }
      section.dataset.iineClosingReady = 'true';
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initIineCarousels();
    initIineReviewWalls();
    initIineClosingScenes();
    initIineSectionMotion();
    initIineHeroVideoPlayback();
    respectReducedMotion();
  });
  document.addEventListener('shopify:section:load', (event) => {
    initIineCarousels(event.target);
    initIineReviewWalls(event.target);
    initIineClosingScenes(event.target);
    initIineSectionMotion(event.target);
    initIineHeroVideoPlayback(event.target);
  });

  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('.iine-hero__video[data-iine-playback-ready="true"]').forEach((video) => {
      if (document.hidden) {
        video.pause();
        return;
      }
      if (!video.getClientRects().length) return;
      const rect = video.getBoundingClientRect();
      if (rect.bottom > -80 && rect.top < window.innerHeight + 80) video.play().catch(() => {});
    });
  });
})();
